import _ from "lodash";
import { FillLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from "react-map-gl/mapbox";

export const COLOR_PALETTE =
	// ["#e41a1c", "#377eb8", "#984ea3", "#ff7f00", "#a65628", "#f781bf"];
	[
		"#e41a1c",
		"#07a1e8",
		"#9607e8",
		"#e87407",
		"#52ff8c",
		"#e80774",
		"#e807e1",
		"#5adbc4",
		"#64bdd9",
		"#519bdb",
		"#6e69ff",
		"#cd69ff",
		"#ff69da",
		"#ff698f",
		"#ffa078",
		"#c6ed64",
		"#3fbec4",
		"#491bb5",
	];

export const getColorFromPalette = (i: number) => (i >= 0 ? COLOR_PALETTE[i % COLOR_PALETTE.length] : "#888888");

export const makeColorStopsValid = (stops: ColorStop[]) => {
	const stopsMap = _.reduce(
		stops,
		(acc, { color, stop }) => {
			acc[stop] = color;
			return acc;
		},
		{} as { [key: number]: string },
	);

	const validStops = _.map(stopsMap, (color, stop) => {
		return { color, stop: Number(stop) } as ColorStop;
	});

	return validStops;
};

type FillLayerProps = Omit<FillLayerSpecification, "source">;
type LineLayerProps = Omit<LineLayerSpecification, "source">;
type SymbolLayerProps = Omit<SymbolLayerSpecification, "source">;

export const plotLayer: FillLayerProps = {
	id: "plot-fill",
	type: "fill",
	paint: {
		"fill-opacity": 0,
	},
};

export const outlinePlotLayer: LineLayerProps = {
	id: "plot-outlines",
	type: "line",
	paint: {
		"line-color": "#0ac944",
		"line-width": 1,
	},
};

export const hoveredPlotLayer: FillLayerProps = {
	id: "plot-hovered",
	type: "fill",
	paint: {
		"fill-color": "#07a1e8",
		"fill-opacity": 0.65,
	},
};

export type ColorStop = {
	color: string;
	stop: number;
};

export const generateHeatmapLayer = (
	colorStops: ColorStop[],
	opacity: number,
	hideExclusions: boolean,
): FillLayerProps => {
	const builtColorStops = _.reduce(
		colorStops,
		(acc, cs) => {
			acc.push(cs.stop, cs.color);
			return acc;
		},
		[] as (string | number)[],
	);

	return {
		id: "heatmap-fill",
		type: "fill",
		paint: {
			"fill-color":
				builtColorStops.length > 0
					? hideExclusions
						? [
								"case",
								["==", ["get", "status"], "Excluded"],
								"#808080",
								// ["==", ["get", "status"], "PartiallyExcluded"],
								// "#808080",
								["!=", ["get", "value"], null],
								["interpolate", ["linear"], ["get", "value"], ...builtColorStops],
								"#808080",
							]
						: [
								"case",
								["!=", ["get", "value"], null],
								["interpolate", ["linear"], ["get", "value"], ...builtColorStops],
								"#808080",
							]
					: "transparent",
			"fill-opacity": opacity,
			"fill-opacity-transition": {
				duration: 0,
			},
			"fill-color-transition": {
				duration: 0,
			},
		},
	};
};

export const annotationLayer: SymbolLayerProps = {
	id: "annotation-icons",
	type: "symbol",
	filter: ["has", "statusIcon"],
	layout: {
		"icon-image": ["get", "statusIcon"],
		"icon-size": ["interpolate", ["exponential", 2], ["zoom"], 14, 0, 22, ["get", "iconScaleFactor"]],
		"icon-allow-overlap": true,
	},
	paint: {
		"icon-color": ["case", ["in", "Flagged", ["get", "status"]], "#ffa500", "#FF0000"],
	},
};

// export const plotLabelLayer: SymbolLayerProps = {
// 	id: "plot-label",
// 	type: "symbol",
// 	filter: ["has", "plotName"],
// 	layout: {
// 		"text-field": ["format", ["get", "plotName"]],
// 		"text-variable-anchor": ["center"],
// 		"text-size": [
// 			"interpolate",
// 			["exponential", 2],
// 			["zoom"],
// 			14,
// 			3,
// 			19,
// 			["*", ["min", ["get", "plotColumnWidth"], ["get", "plotRangeLength"]], 0.8],
// 			25,
// 			255
// 		],
// 		"text-justify": "auto"
// 	},
// 	paint: {
// 		"text-color": "#ffffff",
// 		"text-halo-color": "#000000",
// 		"text-halo-width": 1
// 	}
// };

export const generatePlotLabelLayer = (prop: string): SymbolLayerProps => ({
	id: "plot-label",
	type: "symbol",
	filter: ["has", "plotName"],
	layout: {
		"text-field": ["format", ["get", prop]],
		"text-variable-anchor": ["center"],
		"text-size": [
			"interpolate",
			["exponential", 2],
			["zoom"],
			14,
			3,
			19,
			["*", ["min", ["get", "plotColumnWidth"], ["get", "plotRangeLength"]], 0.8],
			25,
			255,
		],
		"text-justify": "auto",
	},
	paint: {
		"text-color": "#ffffff",
		"text-halo-color": "#000000",
		"text-halo-width": 1,
	},
});

export const generateZoneLayer = (opacity: number): FillLayerProps => {
	return {
		id: "zone-fill",
		type: "fill",
		paint: {
			"fill-color": ["get", "color"],
			"fill-opacity": opacity,
			"fill-opacity-transition": {
				duration: 0,
			},
			"fill-color-transition": {
				duration: 0,
			},
		},
	};
};

export const generateZonePatternLayer = (opacity: number): FillLayerProps => {
	return {
		id: "zone-fill-pattern",
		type: "fill",
		paint: {
			"fill-pattern": ["get", "pattern"],
			"fill-opacity": opacity,
			"fill-opacity-transition": {
				duration: 0,
			},
			"fill-color-transition": {
				duration: 0,
			},
		},
	};
};
